import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Chip,
    IconButton,
    Grid,
    Paper,
    Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

 
import { useDispatch, useSelector } from "react-redux";
import { getAllMandalsAction } from "../../../store/actions";
import AddMandal from "./AddMandal";

const Mandal = () => {
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedMandalId, setSelectedMandalId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 30;
    const dispatch = useDispatch();

    // Reference lists
    const { successData: mandalsData, loading: mandalsLoading } = useSelector((state) => state.getAllMandalsReducer || {});

    // Create/Update/ById reducers


    const mandals = useMemo(() => {
        if (Array.isArray(mandalsData)) return mandalsData;
        if (mandalsData && mandalsData.data && Array.isArray(mandalsData.data.mandals)) return mandalsData.data.mandals;
        return [];
    }, [mandalsData]);

    // Search filter across id/name/districtId/districtName/status
    const filteredMandals = useMemo(() => {
        const q = (searchQuery || "").toString().trim().toLowerCase();
        if (!q) return mandals;
        return mandals.filter((m) => {
            const id = (m.mandalId || m.id || m.mandal_id || "").toString().toLowerCase();
            const name = (m.name || m.mandal_name || m.mandalName || "").toString().toLowerCase();
            const districtId = (m.district_id || m.districtId || m.districtID || "").toString().toLowerCase();
            const districtName = (m.district_name || m.districtName || "").toString().toLowerCase();
            const status = (m.status || "").toString().toLowerCase();
            return id.includes(q) || name.includes(q) || districtId.includes(q) || districtName.includes(q) || status.includes(q);
        });
    }, [mandals, searchQuery]);

    // Normalize mandal fields once for rendering
    const normalizeMandal = (m) => ({
        id: m.mandalId || m.id || m.mandal_id || "",
        name: m.name || m.mandal_name || m.mandalName || "",
        districtId: m.district_id || m.districtId || m.districtID || "",
        districtName: m.district_name || m.districtName || (m.district_id || m.districtId || m.districtID || ""),
        status: m.status || "Active",
        raw: m,
    });

    const normalizedMandals = useMemo(() => filteredMandals.map(normalizeMandal), [filteredMandals]);

    useEffect(() => { setPage(1); }, [searchQuery]);

    const totalPages = Math.max(1, Math.ceil(normalizedMandals.length / PAGE_SIZE));
    const pagedMandals = normalizedMandals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // const mandalSchema = Yup.object({

    useEffect(() => {
        // Fetch reference lists and mandals on mount
        dispatch(getAllMandalsAction());
    }, [dispatch]);

    // Handlers and helpers
    const handleAdd = () => {
        setIsEdit(false);
        setSelectedMandalId("");
        setOpen(true);
    };

    const handleEdit = (mandal) => {
        const mandalId = mandal.mandalId || mandal.id || mandal.mandal_id || mandal.mandalID;
        if (!mandalId) return;
        setIsEdit(true);
        setSelectedMandalId(String(mandalId));
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedMandalId("");
    };

    const getStatusChipStyles = (status) => {
        const s = (status || '').toString().toLowerCase();
        if (s === 'active') return { backgroundColor: '#D1FAE5', color: '#047857' };
        if (s === 'inactive') return { backgroundColor: '#FECACA', color: '#B91C1C' };
        if (s === 'pending') return { backgroundColor: '#FDE68A', color: '#92400E' };
        return { backgroundColor: '#E5E7EB', color: '#111827' };
    };

    return (
        <Box sx={{ minHeight: '100vh', mt: 2 }}>
            <Typography fontSize={18} fontWeight={600} color="#111827" mb={2}>
                Mandal Management
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 3,
                    width: { xs: 330, sm: 'auto' },
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                <TextField
                    placeholder="Search ..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        width: { xs: 300, sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                        },
                        '& .MuiInputBase-input': {
                            borderRadius: 3,
                        },
                    }}
                />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: "#4F46E5",
                        textTransform: "none",
                        borderRadius: 3,
                        width:{xs:300},
                        px: 3,
                    }}
                    onClick={handleAdd}
                >
                    Add New Mandal
                </Button>
            </Box>

            {/* Table for sm+ screens - Zone-style grid */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {/* Header */}
                <Box
                    display={{ xs: 'none', sm: 'grid' }}
                    gridTemplateColumns={{ sm: '1fr 1.6fr 1fr 1fr 1fr' }}
                    alignItems="center"
                    px={{ xs: 1, sm: 2 }}
                    py={{ xs: 1, sm: 2 }}
                    mb={{ xs: 1, sm: 1 }}
                >
                    {['MANDAL ID', 'MANDAL NAME', 'DISTRICT', 'STATUS', 'ACTIONS'].map((label, i) => (
                        <Typography
                            key={label}
                            fontSize={{ sm: 10, md: 12 }}
                            color="#000000"
                            fontWeight={700}
                            sx={{
                                textTransform: 'uppercase',
                                textAlign: i === 3 ? 'center' : i === 4 ? 'right' : 'left',
                            }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                {mandalsLoading && (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', px: 2, py: 1.5, mb: 2 }}>
                        <Typography variant="body2">Loading mandals...</Typography>
                    </Paper>
                )}
                {!mandalsLoading && pagedMandals.map(({ id: mandalId, name, districtName, status, raw }) => {
                    const chipSx = {
                        width: 90,
                        height: 24,
                        ...getStatusChipStyles(status),
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                    };
                    return (
                        <Paper
                            key={mandalId || name}
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: '1fr 1.6fr 1fr 1fr 1fr' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                px: 2,
                                py: 1.5,
                                gap: 0,
                                columnGap: 0,
                                mb: 1.25,
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{mandalId}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{name}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{districtName}</Typography>
                            <Box sx={{ justifySelf: 'center' }}>
                                <Chip label={String(status).charAt(0).toUpperCase() + String(status).slice(1)} size="small" sx={chipSx} />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton size="small" aria-label="edit mandal" onClick={() => handleEdit(raw)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
                {!mandalsLoading && totalPages > 1 && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                        <Typography variant="body2" >
                            Showing {Math.min((page - 1) * PAGE_SIZE + 1, normalizedMandals.length)} to {Math.min(page * PAGE_SIZE, normalizedMandals.length)} of {normalizedMandals.length} entries
                        </Typography>
                        <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
                    </Box>
                )}
            </Box>

            {/* Cards for xs screens */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }} justifyContent="center">
                {!mandalsLoading && pagedMandals.map(({ id: mandalId, name, districtName, status, raw }) => {
                    return (
                        <Grid item xs={12} key={mandalId || name} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    width: 300,
                                    backgroundColor: '#fff',
                                    border: '1px solid #E5E7EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    '&:hover': {
                                        border: '2px solid #2563EB',
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                                    <Box />
                                    <Chip
                                        label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                                        size="small"
                                        sx={{ ...getStatusChipStyles(status), fontWeight: 600 }}
                                    />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" fontWeight={600} color="#0F172A">Mandal: {name}</Typography>
                                    <Typography variant="body2" fontWeight={600} color="#64748B">Mandal ID: {mandalId}</Typography>
                                    <Typography variant="body2" fontWeight={600} color="#64748B">District: {districtName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <IconButton size="small" aria-label="edit mandal" onClick={() => handleEdit(raw)}>
                                        <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB' }} />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
                {!mandalsLoading && totalPages > 1 && (
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                            <Typography variant="body2" >
                                Showing {Math.min((page - 1) * PAGE_SIZE + 1, normalizedMandals.length)} to {Math.min(page * PAGE_SIZE, normalizedMandals.length)} of {normalizedMandals.length} entries
                            </Typography>
                            <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
                        </Box>
                    </Grid>
                )}
            </Grid>

            <AddMandal
                open={open}
                onClose={handleCloseDialog}
                isEdit={isEdit}
                mandalId={selectedMandalId}
                onSuccess={() => dispatch(getAllMandalsAction())}
            />
        </Box>
    );
};

export default Mandal;
